import { Device } from "@twilio/voice-sdk";
import api from "@/lib/api";

/**
 * Manage a singleton Twilio.Device per logged-in user.
 * Returns helpers used by the Dialer when the active provider is "twilio".
 */
class TwilioDeviceManager {
    constructor() {
        this.device = null;
        this.identity = null;
        this.activeCall = null;
        this.incomingCall = null;
        this.autoAccept = false;
        this.listeners = new Set();
    }

    on(fn) {
        this.listeners.add(fn);
        return () => this.listeners.delete(fn);
    }

    emit(event, payload = {}) {
        for (const fn of this.listeners) fn({ event, ...payload });
    }

    async ensureDevice(identity) {
        console.log("[twilioDevice] ensureDevice called with identity=", identity, "current state:", this.device?.state);
        if (this.device && this.identity === identity) return this.device;
        // Guard against concurrent calls (the init effect can fire more than once)
        // creating multiple orphaned Device instances that flap register/unregister.
        if (this._ensuring && this._ensuringIdentity === identity) return this._ensuring;
        this._ensuringIdentity = identity;
        this._ensuring = this._createDevice(identity);
        try {
            return await this._ensuring;
        } finally {
            this._ensuring = null;
            this._ensuringIdentity = null;
        }
    }

    async _createDevice(identity) {
        if (this.device) {
            try { this.device.destroy(); } catch (_e) {}
            this.device = null;
        }
        this.identity = identity;
        const { data } = await api.post("/twilio/token", { identity });
        console.log("[twilioDevice] got token for", data.identity);
        const device = new Device(data.token, { logLevel: "warn", codecPreferences: ["opus", "pcmu"] });

        device.on("registered", () => { console.log("[twilioDevice] ✅ REGISTERED as", identity); this.emit("registered"); });
        device.on("unregistered", () => { console.log("[twilioDevice] ❌ UNREGISTERED"); this.emit("unregistered"); });
        device.on("error", (err) => { console.error("[twilioDevice] ⚠️ error", err); this.emit("error", { error: err?.message || String(err) }); });
        device.on("tokenWillExpire", async () => {
            try {
                const { data: refreshed } = await api.post("/twilio/token", { identity });
                device.updateToken(refreshed.token);
            } catch (_e) {}
        });
        device.on("incoming", (call) => {
            console.log("[twilioDevice] 🔔 INCOMING CALL", call.parameters);
            this.incomingCall = call;
            const from = call.parameters?.From || "unknown";
            let isTransfer = false;
            let transferFrom = null;
            try {
                const cp = call.customParameters;
                if (cp && typeof cp.get === "function") {
                    isTransfer = !!cp.get("is_transfer");
                    transferFrom = cp.get("transfer_from") || null;
                }
            } catch (_e) { /* never let param parsing block the incoming event */ }
            call.on("accept", () => { this.activeCall = call; this.incomingCall = null; this.emit("call:accept", { sid: call.parameters?.CallSid, inbound: true }); });
            call.on("disconnect", () => { this.activeCall = null; this.incomingCall = null; this.emit("call:disconnect"); });
            call.on("cancel", () => { console.log("[twilioDevice] call canceled"); this.incomingCall = null; this.emit("call:cancel"); });
            call.on("reject", () => { this.incomingCall = null; this.emit("call:reject"); });
            const autoAccepted = !!this.autoAccept && !isTransfer;
            this.emit("incoming", { from, callSid: call.parameters?.CallSid, isTransfer, transferFrom, autoAccepted });
            if (autoAccepted) {
                // Predictive dialer: connect the routed call to the agent automatically.
                // Re-bind audio devices first so the auto-accepted call has working
                // two-way audio even though the agent never placed an outbound call.
                console.log("[twilioDevice] auto-accepting predictive call");
                this.primeAudio().catch(() => {}).finally(() => {
                    try { call.accept(); } catch (e) { console.error("[twilioDevice] auto-accept failed", e); }
                });
            }
        });

        this.device = device;
        await device.register();
        return device;
    }

    /**
     * Recreate the Device after mic permission is granted so its AudioHelper
     * enumerates real input/output devices. Predictive agents never place an
     * outbound call, so the Device is first created WITHOUT permission — its
     * device list is empty ("Devices not found: default") and audio is dead.
     */
    async reinit(identity) {
        this.identity = null; // force ensureDevice to rebuild
        return this.ensureDevice(identity);
    }

    /** Bind the mic + speaker on the current Device (after permission is granted). */
    async primeAudio() {
        const dev = this.device;
        const audio = dev && dev.audio;
        if (!audio) return;
        try {
            const inputs = audio.availableInputDevices;
            if (inputs && inputs.size) {
                const inId = inputs.has("default") ? "default" : inputs.keys().next().value;
                await audio.setInputDevice(inId);
                console.log("[twilioDevice] input device bound:", inId);
            }
        } catch (e) { console.warn("[twilioDevice] setInputDevice failed", e?.message || e); }
        try {
            if (audio.isOutputSelectionSupported && audio.speakerDevices) {
                const outs = audio.availableOutputDevices;
                if (outs && outs.size) {
                    const outId = outs.has("default") ? "default" : outs.keys().next().value;
                    await audio.speakerDevices.set(outId);
                    console.log("[twilioDevice] speaker device bound:", outId);
                }
            }
        } catch (e) { console.warn("[twilioDevice] speakerDevices.set failed", e?.message || e); }
    }

    requestDial(number, opts = {}) {
        // Used by other UI surfaces (Call History, Dashboard recent activity, etc.)
        // to programmatically open the floating dialer with a number prefilled.
        this.emit("dialer:open", { number, autoDial: !!opts.autoDial, campaign: !!opts.campaign });
    }

    /** Browser-console debugging helper. */
    debugState() {
        return {
            identity: this.identity,
            deviceState: this.device?.state,
            isReady: this.isReady(),
            hasIncoming: !!this.incomingCall,
            hasActive: !!this.activeCall,
        };
    }

    acceptIncoming() {
        if (this.incomingCall) {
            try { this.incomingCall.accept(); } catch (_e) {}
        }
    }

    setAutoAccept(v) { this.autoAccept = !!v; }

    rejectIncoming() {
        if (this.incomingCall) {
            try { this.incomingCall.reject(); } catch (_e) {}
            this.incomingCall = null;
        }
    }

    async connect({ to_number, internal_call_id }) {
        if (!this.device) throw new Error("Twilio Device not ready");
        // The Voice SDK requires at least one param to make the call.
        // We pass a placeholder + the internal_call_id. The server ignores the
        // placeholder and looks up the real to_number from MongoDB by id.
        console.log("[twilioDevice] connect:", { internal_call_id, to_number });
        const call = await this.device.connect({
            params: { To: "lookup", internal_call_id },
        });
        console.log("[twilioDevice] device.connect ok, call:", call?.parameters);
        this.activeCall = call;

        call.on("accept", () => this.emit("call:accept", { sid: call.parameters?.CallSid }));
        call.on("ringing", () => this.emit("call:ringing"));
        call.on("disconnect", () => {
            this.activeCall = null;
            this.emit("call:disconnect");
        });
        call.on("cancel", () => {
            this.activeCall = null;
            this.emit("call:cancel");
        });
        call.on("error", (err) => this.emit("call:error", { error: err?.message || String(err) }));

        return call;
    }

    disconnect() {
        if (this.activeCall) {
            try { this.activeCall.disconnect(); } catch (_e) {}
            this.activeCall = null;
        }
    }

    /** Supervisor joins a live call's conference muted (listen-only). */
    async connectMonitor(room) {
        if (!this.device) throw new Error("Twilio Device not ready");
        const call = await this.device.connect({ params: { To: "monitor", monitor_room: room } });
        this.monitorCall = call;
        call.on("disconnect", () => { this.monitorCall = null; this.emit("monitor:end"); });
        call.on("error", (err) => this.emit("monitor:error", { error: err?.message || String(err) }));
        return call;
    }

    stopMonitor() {
        if (this.monitorCall) {
            try { this.monitorCall.disconnect(); } catch (_e) {}
            this.monitorCall = null;
        }
    }

    isMonitoring() {
        return !!this.monitorCall;
    }

    /** SID of the agent's current active call leg (for transfer leg resolution). */
    getActiveCallSid() {
        return this.activeCall?.parameters?.CallSid || null;
    }

    mute(muted) {
        if (this.activeCall) {
            try { this.activeCall.mute(muted); } catch (_e) {}
        }
    }

    isReady() {
        return !!this.device && this.device.state === "registered";
    }
}

const twilioDevice = new TwilioDeviceManager();
if (typeof window !== "undefined") {
    window.twilioDevice = twilioDevice; // for browser-console debugging
}
export default twilioDevice;
